<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- ============================================================
                 HERO
                 ============================================================ -->
            <section class="mkt-hero-light mkt-hero-watermark" style="padding:80px 0 50px;">
                <div class="mkt-hero-watermark-icon mkt-hero-watermark-icon--right" aria-hidden="true">
                    <q-icon name="hub" />
                </div>
                <div class="mkt-container text-center" style="position:relative;z-index:1;">
                    <span class="mkt-trust-pill mkt-trust-pill--light q-mb-md" style="display:inline-block;">
                        <q-icon name="hub" class="mkt-trust-icon" /> HubSpot Marketplace integration
                    </span>
                    <h1 class="text-weight-bold q-mt-md q-mb-md" style="font-size:clamp(2rem,4vw,3rem);color:#0F172A;line-height:1.15;">
                        Send for signature<br>
                        <span class="text-gradient-indigo">straight from a HubSpot Workflow.</span>
                        <span class="visually-hidden"> eSign Launchpad HubSpot integration with workflow actions and timeline events.</span>
                    </h1>
                    <p class="q-mb-lg" style="font-size:1.1rem;max-width:680px;margin:0 auto;color:#475569;">
                        When a Deal moves to Contract Sent, fire an eSign Launchpad envelope automatically.
                        When the document is signed, the PDF lands on the Contact's timeline.
                        No CSV exports, no manual follow-up.
                    </p>
                    <div class="row justify-center q-gutter-md mkt-hero-cta-row">
                        <q-btn color="primary" size="lg" no-caps unelevated icon-right="arrow_forward"
                               label="Start Free"
                               href="/Account/Register"
                               style="border-radius:10px;padding:10px 32px;font-weight:600;" />
                        <q-btn outline size="lg" no-caps label="See how it works" href="#workflow"
                               style="border-radius:10px;padding:10px 32px;color:#334155;border-color:#CBD5E1;" />
                    </div>
                    <div class="text-grey-7 q-mt-md" style="font-size:0.85rem;">
                        Already have an account? <a href="/integrations/hubspot/authorize" class="text-primary">Connect your HubSpot portal</a>.
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 WORKFLOW: 3 step
                 ============================================================ -->
            <section id="workflow" class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">How it works</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">From CRM stage change to signed document, no copy-paste.</h2>
                    </div>
                    <div class="row q-col-gutter-xl items-stretch">
                        <div class="col-12 col-md-4" v-for="(step, i) in steps" :key="i">
                            <div class="mkt-step-tile">
                                <div class="mkt-step-ribbon">{{ String(i + 1).padStart(2, '0') }}</div>
                                <div class="row items-center q-gutter-sm q-mb-sm">
                                    <q-icon :name="step.icon" size="28px" color="primary" />
                                    <h3 class="text-weight-bold q-ma-none" style="font-size:1.2rem;color:#0a0f1a;line-height:1.2;">{{ step.title }}</h3>
                                </div>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.6;">{{ step.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 WORKFLOW ACTIONS
                 ============================================================ -->
            <section class="mkt-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">Workflow actions</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">Four actions you can drop into any HubSpot Workflow</h2>
                        <p class="text-grey-7 q-mt-sm" style="font-size:1rem;max-width:620px;margin:0 auto;">
                            Each action runs natively inside HubSpot's workflow engine. Wire any of them to a Contact, Deal, or Ticket workflow with HubSpot's normal trigger criteria.
                        </p>
                    </div>
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-6" v-for="action in actions" :key="action.title">
                            <div class="mkt-card q-pa-lg" style="height:100%;">
                                <q-item class="q-pa-none q-mb-sm">
                                    <q-item-section avatar>
                                        <q-icon :name="action.icon" size="32px" :style="`color:${action.color};`" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.1rem;color:#0a0f1a;line-height:1.25;">{{ action.title }}</q-item-label>
                                        <q-item-label caption class="text-grey-7" style="font-size:0.8rem;">{{ action.subtitle }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.6;">{{ action.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 TIMELINE / FILE WRITEBACK
                 ============================================================ -->
            <section class="mkt-white reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">Two-way sync</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">Lifecycle events thread back onto the Contact's timeline.</h2>
                    </div>
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-6 col-lg-3" v-for="evt in writebackEvents" :key="evt.title">
                            <div class="mkt-card q-pa-lg" style="height:100%;">
                                <q-icon :name="evt.icon" size="36px" :style="`color:${evt.color};`" class="q-mb-sm" />
                                <div class="text-weight-bold q-mb-xs" style="font-size:1rem;color:#0a0f1a;">{{ evt.title }}</div>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.9rem;line-height:1.55;">{{ evt.detail }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="text-center q-mt-lg">
                        <p class="text-grey-7" style="font-size:0.9rem;max-width:680px;margin:0 auto;">
                            Read-or-skip privacy posture: events and file attachments only land on Contacts that already exist.
                            Mismatched signer emails are silently skipped.
                            We never auto-create HubSpot records on your behalf.
                        </p>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 INSTALL STEPS
                 ============================================================ -->
            <section class="mkt-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">Install</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">Three steps. About sixty seconds.</h2>
                    </div>
                    <div class="row q-col-gutter-xl items-stretch">
                        <div class="col-12 col-md-4" v-for="(step, i) in installSteps" :key="i">
                            <div class="mkt-step-tile">
                                <div class="mkt-step-ribbon">{{ String(i + 1).padStart(2, '0') }}</div>
                                <h3 class="text-weight-bold q-mb-sm" style="font-size:1.1rem;color:#0a0f1a;">{{ step.title }}</h3>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.6;">{{ step.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 SECURITY POSTURE
                 ============================================================ -->
            <section class="mkt-white reveal-fade-in" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container-narrow">
                    <div class="mkt-card q-pa-xl" style="border-left:4px solid var(--mkt-indigo);">
                        <div class="text-weight-bold q-mb-md" style="font-size:1.3rem;color:#0F172A;">Security posture</div>
                        <ul class="q-mb-none" style="font-size:0.95rem;line-height:1.8;color:#334155;padding-left:18px;">
                            <li v-for="bullet in securityBullets" :key="bullet">{{ bullet }}</li>
                        </ul>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 PRICING
                 ============================================================ -->
            <section id="pricing" class="mkt-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-lg">
                        <div class="mkt-section-eyebrow">Pricing</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">No HubSpot upcharge.</h2>
                        <p class="text-grey-7" style="font-size:1rem;max-width:560px;margin:0 auto;">
                            Use any eSign Launchpad plan with the HubSpot integration at no extra cost.
                            Free tier covers personal use, paid tiers scale with package volume.
                        </p>
                    </div>
                    <div class="text-center q-mt-md">
                        <q-btn unelevated color="primary" no-caps label="Compare all plans" href="/Home/Pricing"
                               icon-right="arrow_forward"
                               style="border-radius:10px;padding:10px 28px;font-weight:500;" />
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 FAQ
                 ============================================================ -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0 80px;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">FAQ</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:1.8rem;color:#0a0f1a;">Common questions about the HubSpot app</h2>
                    </div>
                    <q-list class="rounded-borders">
                        <q-expansion-item v-for="(faq, i) in faqs" :key="i"
                                          :label="faq.q" expand-separator>
                            <q-card>
                                <q-card-section class="text-grey-7" style="font-size:0.95rem;line-height:1.7;">
                                    {{ faq.a }}
                                </q-card-section>
                            </q-card>
                        </q-expansion-item>
                    </q-list>
                </div>
            </section>

            <!-- ============================================================
                 FINAL CTA
                 ============================================================ -->
            <section class="mkt-cta-gradient reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-md" style="font-size:1.8rem;">Stop copying contracts out of HubSpot.</h2>
                    <p class="text-white q-mb-lg" style="opacity:0.9;font-size:1rem;">Start your free eSign Launchpad account and connect your HubSpot portal in under a minute.</p>
                    <q-btn color="white" text-color="primary" size="lg" label="Start Free" href="/Account/Register"
                        no-caps unelevated icon-right="arrow_forward"
                        style="border-radius:10px;padding:10px 36px;font-weight:600;" />
                </div>
            </section>

            <marketing-footer />
        </q-page-container>
    </q-layout>
</template>

<script>
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingFooter from '@components/marketing/MarketingFooter.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'IntegrationsHubspotPage',
    mixins: [revealMixin],
    components: { MarketingNav, MarketingFooter },
    data() {
        return {
            mobileNav: false,

            steps: [
                {
                    icon: 'rocket_launch',
                    title: 'Stage hits Contract Sent',
                    detail: 'Your existing HubSpot Workflow fires the Send Document for Signature action with a chosen template.'
                },
                {
                    icon: 'mail',
                    title: 'Signer gets the envelope',
                    detail: 'eSign Launchpad emails the signer with a one-click ceremony URL. They sign on any device.'
                },
                {
                    icon: 'inbox',
                    title: 'Signed PDF on the timeline',
                    detail: 'Once complete, the signed PDF lands on the matched HubSpot Contact, plus a timestamped activity note.'
                }
            ],

            actions: [
                {
                    icon: 'send',
                    color: '#2563EB',
                    title: 'Send Document for Signature',
                    subtitle: 'esign_launchpad_send_package',
                    detail: 'Pick a template from a live dropdown of your saved templates. Wire recipientEmail and recipientName to any Contact or Deal property. Optional subject and message overrides on the action itself.'
                },
                {
                    icon: 'block',
                    color: '#DC2626',
                    title: 'Void eSign Package',
                    subtitle: 'esign_launchpad_void_package',
                    detail: 'When a Deal moves to Closed Lost, void the in-flight signature request automatically. Pass a custom void reason that ends up on the audit trail.'
                },
                {
                    icon: 'notifications_active',
                    color: '#7C3AED',
                    title: 'Send eSign Reminder',
                    subtitle: 'esign_launchpad_send_reminder',
                    detail: 'Re-notify every signer who has not yet completed. Use HubSpot delay branches to nudge after 3 days, 7 days, whatever rhythm matches your sales motion.'
                },
                {
                    icon: 'fact_check',
                    color: '#047857',
                    title: 'Get eSign Package Status',
                    subtitle: 'esign_launchpad_get_package_status',
                    detail: 'Read-only lookup that returns package status, completion date, signer count, and document count. Feed it into HubSpot If/Then branches to drive downstream actions.'
                }
            ],

            writebackEvents: [
                {
                    icon: 'send',
                    color: '#2563EB',
                    title: 'Package sent',
                    detail: 'A timeline note when the envelope is dispatched, with package name and recipient list.'
                },
                {
                    icon: 'task_alt',
                    color: '#047857',
                    title: 'Signer completed',
                    detail: 'Each signer\'s completion shows up as its own activity entry with signer name and timestamp.'
                },
                {
                    icon: 'cancel',
                    color: '#B45309',
                    title: 'Signer declined',
                    detail: 'Decline events surface immediately, including any reason the signer entered.'
                },
                {
                    icon: 'block',
                    color: '#DC2626',
                    title: 'Package voided',
                    detail: 'When a sender voids a package, every matched Contact gets a void note with the reason.'
                }
            ],

            installSteps: [
                {
                    title: 'Connect from your dashboard',
                    detail: 'In eSign Launchpad, open Settings then Integrations and click Connect HubSpot. You jump straight to HubSpot\'s OAuth consent screen.'
                },
                {
                    title: 'Approve the scopes',
                    detail: 'Grant read access to Contacts, plus the optional writeback scopes if you want timeline events and property updates. HubSpot redirects back and links your portal to your workspace automatically.'
                },
                {
                    title: 'Drop a workflow action',
                    detail: 'In any HubSpot Workflow, search the action library for eSign Launchpad and pick the action you want. Configure the inputs and turn the workflow on.'
                }
            ],

            securityBullets: [
                'X-HubSpot-Signature-v3 (HMAC-SHA256) on every inbound workflow request, validated on our side before any business logic runs.',
                'OAuth tokens stored AES-256-GCM encrypted with a key kept in Azure Key Vault. Never logged.',
                'Token refresh runs every 15 minutes against HubSpot. Rotated tokens re-encrypt before persistence.',
                'Read-or-skip Contact policy. We never auto-create HubSpot records, even when a signer email could plausibly become one.',
                'On uninstall (HubSpot side or yours), the token row is marked uninstalled, the access token is invalidated, and all background calls stop.',
                'Hosted on Microsoft Azure US infrastructure with TLS 1.3 in transit and AES-256 at rest.'
            ],

            faqs: [
                {
                    q: 'When will the app be on the HubSpot Marketplace?',
                    a: 'It is in HubSpot\'s marketplace review queue. You do not have to wait for it: the integration is fully built and live, so you can connect your HubSpot portal today from Settings then Integrations in your eSign Launchpad dashboard. Once HubSpot completes review, the listing also goes public for one-click install from the App Marketplace.'
                },
                {
                    q: 'Which OAuth scopes does the app request, and why?',
                    a: 'Two required scopes: oauth for token refresh, and crm.objects.contacts.read to look up the signer\'s Contact. Everything else is optional and you can decline it at consent: crm.objects.contacts.write and crm.schemas.contacts.write let us write the esign_* status properties back to the matched Contact, files lets us attach the signed PDF, and timeline lets us post activity events. Decline any optional scope and that feature simply turns off; nothing else breaks.'
                },
                {
                    q: 'Does eSign Launchpad ever create or modify HubSpot Contacts?',
                    a: 'We never create Contacts. If a signer\'s email does not match an existing Contact, we silently skip that signer rather than auto-creating a record. We do modify existing Contacts only when you grant the optional write scopes: we write the esign_* status properties (last status, last signed date, totals) and post timeline events, and only on Contacts that already exist. No-auto-create is an explicit privacy posture, not a coincidence.'
                },
                {
                    q: 'Can I use the integration on my Free HubSpot portal?',
                    a: 'Yes for the Send / Void / Reminder / Status actions on Contact and Ticket workflows. Deal-based workflows require HubSpot Sales Hub Professional or higher because that\'s where HubSpot gates Deal workflows themselves; this is a HubSpot platform constraint, not ours.'
                },
                {
                    q: 'How do you verify that the request really came from HubSpot?',
                    a: 'Every workflow action POST carries an X-HubSpot-Signature-v3 header. We compute HMAC-SHA256 over (method + URL + body + timestamp) using our app\'s client secret. Mismatched signatures are rejected with HTTP 401. Replay protection: requests older than 5 minutes are rejected too.'
                },
                {
                    q: 'What happens if my eSign Launchpad workspace is suspended?',
                    a: 'Workflow actions fail with a clear error in the workflow run history. The HubSpot workflow keeps running through other steps; the eSign action stops the workflow with stopProcessing=true so HubSpot does not retry indefinitely. Reactivate your workspace and the next workflow run will succeed.'
                },
                {
                    q: 'How do I uninstall?',
                    a: 'Two paths: from your HubSpot account under Settings -> Integrations -> Connected Apps, click Uninstall. Or from the eSign Launchpad portal under Settings -> Integrations, click Disconnect on the HubSpot card. Either side flows tokens through to invalidation.'
                }
            ]
        };
    }
};
</script>

<style scoped>
.mkt-section-eyebrow {
    display: inline-block;
    font-size: 0.8rem;
    font-weight: 700;
    color: #4F46E5;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 6px;
}
</style>
